import axios, { AxiosHeaders, CanceledError } from "axios"
import * as R from "remeda"
import { isFileUpload } from "./types"
import { getDebugTokenLink, isAPIError } from "./utils"

export function createClient({
  baseURL,
  token,
  headers: additionalHeaders = new Headers(),
}: CreateClientArgs = {}) {
  const headers = new AxiosHeaders({
    "Content-Type": "application/json",
    Accept: "application/ld+json",

    ...Object.fromEntries(additionalHeaders.entries()),
  })

  if (token) {
    headers.set("Authorization", `Bearer ${token}`)
  }

  const client = axios.create({
    baseURL,
    headers,

    transformRequest: (data, headers) => {
      if (
        headers.get("Content-Type") === "multipart/form-data" &&
        R.isPlainObject(data)
      ) {
        return objectToFormData(data)
      }

      return JSON.stringify(data)
    },
  })

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isAPIError(error)) {
        const debugToken = getDebugTokenLink(error)

        if (!(error instanceof CanceledError)) {
          // eslint-disable-next-line no-console
          console.error("API ERROR", error)
        }

        if (debugToken) {
          // eslint-disable-next-line no-console
          console.debug("Debug URL:", debugToken)
        }
      }

      return Promise.reject(error)
    },
  )

  return client
}

type CreateClientArgs = {
  baseURL?: string
  /**
   * @deprecated use `headers` instead
   */
  token?: string
  headers?: Headers
}

export type Client = ReturnType<typeof createClient>

function objectToFormData(object: Record<string, unknown>) {
  const formData = new FormData()

  R.pipe(
    object,
    R.entries(),
    R.forEach(([key, value]) => {
      if (R.isNullish(value)) {
        return
      }

      if (R.isPlainObject(value)) {
        if (isFileUpload(value)) {
          return formData.append(key, value as any)
        } else {
          return formData.append(key, JSON.stringify(value))
        }
      }

      if (Array.isArray(value)) {
        return formData.append(key, JSON.stringify(value))
      }

      formData.append(key, value as any)
    }),
  )

  return formData
}
